import { Celeriant, type OnlineHandle } from '@celeriant/sdk';
import { project, session } from '@/domain/aggregates';
import { token } from '@/infrastructure/container';
import { ServerError } from '@/domain/errors';
import type { IClock } from './Clock';
import type { IIdentity } from './Identity';
import type { IMetrics } from './Metrics';

declare const __GATEWAY_URL__: string;

// Build-time __GATEWAY_URL__ wins, so a deploy can point at a remote gateway. Empty (the dev
// default) means SAME-ORIGIN: the wire client builds /api/v1/... against the page origin and vite
// proxies it, so there is no CORS and the gateway needs no TLS. The loopback fallback is for
// non-browser contexts.
function resolveGatewayUrl(): string {
  const fromDefine = typeof __GATEWAY_URL__ === 'string' ? __GATEWAY_URL__ : '';
  if (fromDefine !== '') return fromDefine;
  if (typeof window !== 'undefined' && window.location !== undefined) return window.location.origin;
  return 'http://127.0.0.1:5200';
}

// Native EventSource in the browser and Node 22+, a no-op where it is absent so the SDK still
// constructs cleanly. The wire layer accepts anything with addEventListener/close.
type EventSourceLike = { addEventListener: (...a: unknown[]) => void; close: () => void };
const noopEventSourceFactory = (_url: string): EventSourceLike => ({
  addEventListener: () => {},
  close: () => {},
});
function resolveEventSourceFactory(): (url: string) => EventSourceLike {
  const ctor = (globalThis as { EventSource?: new (u: string) => EventSourceLike }).EventSource;
  if (ctor === undefined) return noopEventSourceFactory;
  return (url: string) => new ctor(url);
}

const APP_NAME = 'grading-day';

// Pattern B handles. `OnlineHandle`, not `PersistentHandle`: an online aggregate has no optimistic
// commit, no outbox and no undo stack, and `execute()` is the only write. That narrower type is
// the SDK telling the truth about what this app can do (docs/sync.md).
export type SessionHandle = OnlineHandle<typeof session>;
export type ProjectHandle = OnlineHandle<typeof project>;

export interface CeleriantClientOptions {
  // Override the gateway base URL. Node tests pass a direct loopback URL; the browser leaves this
  // undefined and resolves same-origin via the vite define.
  gatewayUrl?: string;
  fetchFn?: typeof globalThis.fetch;
  eventSourceFactory?: (url: string) => EventSourceLike;
  // Override the app name, which is also the IndexedDB database name (`celeriant:<appName>`). The
  // app never sets it. The live two-device test does: two clients in one process sharing one store
  // would share one persisted client id and stop being two devices.
  appName?: string;
}

export interface ICeleriantClient {
  ready(): Promise<void>;
  // One session's roster, votes, attributes and move lists. Every device in the hall opens the
  // same one. The first execute() creates the aggregate and makes that device its owner; there is
  // no seeding step (docs/sync.md, "Creating a session aggregate").
  sessionHandle(id: string): Promise<SessionHandle>;
  // The grading day: the run list and the share-link registry. P2's screens write it.
  projectHandle(id: string): Promise<ProjectHandle>;
  // Turn a scanned bearer key into a persistent grant for this device's keypair. BEFORE the
  // matching handle opens, always: opening an online handle subscribes to realtime immediately and
  // the realtime endpoint takes no share parameter, so an ungranted client is refused and the
  // open resolves to a silently empty handle (docs/access.md, "Redemption ordering").
  //
  // Two methods rather than one taking a definition, so that no caller above this layer has to
  // hold an SDK aggregate definition to redeem (INVARIANTS rule 22).
  redeemProjectShare(id: string, shareKey: string): Promise<void>;
  redeemSessionShare(id: string, shareKey: string): Promise<void>;
  close(): Promise<void>;
}

// Owns the single Celeriant instance: gateway URL resolution, aggregate registration, auth wiring.
// Nothing above this layer imports @celeriant/sdk except for types.
//
// This is the app's I/O seam, so it is where telemetry lives (INVARIANTS rule 21). A store above
// it stays silent; when a red run says a boot failed, `celeriant_ops_total{op,outcome}` says which
// op and the histogram says how long it hung before it gave up.
export class CeleriantClient implements ICeleriantClient {
  private celeriant: Celeriant | undefined;
  private readyPromise: Promise<void> | undefined;

  constructor(
    private readonly identity: IIdentity,
    private readonly clock: IClock,
    private readonly metrics: IMetrics,
    private readonly options: CeleriantClientOptions = {},
  ) {}

  // Idempotent and single-flight: concurrent boot paths share one keypair generation and one IDB
  // open. Call it once during boot, before any store opens a handle.
  ready(): Promise<void> {
    this.readyPromise ??= this.measure('ready', this.boot);
    return this.readyPromise;
  }

  private boot = async (): Promise<void> => {
    await this.identity.ensure();
    const c = new Celeriant({
      appName: this.options.appName ?? APP_NAME,
      gateway: this.options.gatewayUrl ?? resolveGatewayUrl(),
      auth: this.identity.auth,
      aggregates: [project, session],
      ...(this.options.fetchFn !== undefined ? { fetch: this.options.fetchFn } : {}),
      eventSourceFactory: this.options.eventSourceFactory ?? resolveEventSourceFactory(),
    });
    await c.ready();
    this.celeriant = c;
  };

  sessionHandle = async (id: string): Promise<SessionHandle> => {
    await this.ready();
    return this.measure('handle', async () => {
      if (this.celeriant === undefined) throw new ServerError('CeleriantClient: not ready');
      return this.celeriant.aggregateOnline(session, id);
    });
  };

  projectHandle = async (id: string): Promise<ProjectHandle> => {
    await this.ready();
    return this.measure('handle', async () => {
      if (this.celeriant === undefined) throw new ServerError('CeleriantClient: not ready');
      return this.celeriant.aggregateOnline(project, id);
    });
  };

  // One share-scoped read the gateway consumes into a persistent grant. Not retried on failure: the
  // single use may already have been burned server-side and the client cannot tell (the SDK says so
  // in its own doc comment).
  redeemProjectShare = async (id: string, shareKey: string): Promise<void> => {
    await this.ready();
    return this.measure('redeem', async () => {
      if (this.celeriant === undefined) throw new ServerError('CeleriantClient: not ready');
      await this.celeriant.redeemShare(project, id, { shareKey });
    });
  };

  redeemSessionShare = async (id: string, shareKey: string): Promise<void> => {
    await this.ready();
    return this.measure('redeem', async () => {
      if (this.celeriant === undefined) throw new ServerError('CeleriantClient: not ready');
      await this.celeriant.redeemShare(session, id, { shareKey });
    });
  };

  close = async (): Promise<void> => {
    await this.celeriant?.close();
    this.celeriant = undefined;
    this.readyPromise = undefined;
  };

  // Labels stay bounded: `op` is one of three literals and `outcome` one of two. An id or a message
  // here would mint a new series per call and never stop growing (rule 21).
  private measure = async <T>(op: string, run: () => Promise<T>): Promise<T> => {
    const started = this.clock.nowMs();
    try {
      const result = await run();
      this.metrics.counter('celeriant_ops_total', { op, outcome: 'ok' });
      return result;
    } catch (cause) {
      this.metrics.counter('celeriant_ops_total', { op, outcome: 'error' });
      throw cause;
    } finally {
      this.metrics.histogram('celeriant_op_duration_ms', this.clock.nowMs() - started, { op });
    }
  };
}

export const TOKEN_ICeleriantClient = token<ICeleriantClient>('ICeleriantClient');
